import type { ActionType } from 'typesafe-actions';
import { put, select, takeLatest, delay, all, takeEvery } from 'redux-saga/effects';
// eslint-disable-next-line import/no-extraneous-dependencies
import _ from 'lodash';

import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmPlanFind } from 'APIPackage/KnmModule/interfaces/IKnmPlanFind';
import { IResponseError } from 'APIPackage/MainModule/interfaces/IResponseError';
import { IKnmExclusionReasonSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmExclusionReasonSelectOption';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import {
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS as HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS as HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS,
  PLANNED_KNM_CREATED_LATE,
  KNM_RESULT_FILLED_EARLY,
  SNAPSHOT_NOT_FOUND,
  KNM_NOT_AVAILABLE_FOR_CHANGE,
  LINKED_KNM_ERROR,
  DUPLICATE_KEY_REASON_TYPE,
  INN_NOT_MATCH_OGRN,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS,
  KNM_OUT_OF_MEMORY,
  KNM_OUT_OF_MEMORY_KAFKA,
  SEVERAL_REASONS_ARE_FORBIDDEN,
} from 'CorePackage/MainModule/constants/httpErrors';
import {
  CHECK_KNM_BEFORE_SIGN_ERROR,
  CHECK_KNM_BEFORE_SIGN_NOT_SAVED_WARNING,
  CHECK_KNM_BEFORE_SIGN_WARNING,
  CHECK_PM_BEFORE_SIGN_ERROR,
  CHECK_PM_BEFORE_SIGN_NOT_SAVED_WARNING,
  CHECK_PM_BEFORE_SIGN_WARNING,
  CRIPTOPRO_NOT_EXISTS_ERROR,
  DUPLICATE_KEY_REASON_TYPE_ERROR,
  INVALID_DOCUMENT_SIGNATURE,
  INTERNAL_SERVER_ERROR,
  KNM_COPY_SUCCESS,
  KNM_LINK_ERROR,
  KNM_NOT_AVAILABLE_FOR_CHANGE_MESSAGE,
  KNM_NOT_AVAILABLE_LINK_TO_DELETED_PLAN,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_CONSIDERED,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_LESS_30_DAYS,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_UNDER_CONSIDERATION,
  KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS as NOTICE_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS,
  KNM_OUT_OF_MEMORY_MESSAGE,
  KNM_RESTORE_SUCCESS,
  KNM_RESULT_FILLED_EARLY_ERROR_MESSAGE,
  KNM_SAVE_ERROR_WARNING,
  KNM_SAVE_REJECT_REASONS_ERROR_WARNING,
  KNM_SAVE_SUCCESS,
  KNM_SIGN_ADD_SUCCESS,
  KNM_SIGN_DELETE_SUCCESS,
  KNM_UNLIST_ERROR,
  KNM_UNLIST_SUCCESSFULLY,
  PASS_SIGNATURE_VALIDATION,
  PLANNED_KNM_CREATED_LATE as NOTICE_PLANNED_KNM_CREATED_LATE,
  PLANNED_KNM_CREATED_LATE_LESS_30_DAYS,
  PM_COPY_SUCCESS,
  PM_RESTORE_SUCCESS,
  PM_SAVE_ERROR_WARNING,
  PM_SAVE_SUCCESS,
  PM_SIGN_ADD_SUCCESS,
  SEVERAL_REASONS_ARE_FORBIDDEN_MESSAGE,
  CHECK_GENERATE_DECISION,
  CHECK_GENERATE_DECISION_NOT_SAVED_WARNING,
  CHECK_GENERATE_WARNING_NOT_SAVED_WARNING,
  CHECK_GENERATE_WARNING,
  CHECK_GENERATE_EXTRACT,
  CHECK_GENERATE_STATEMENT_WARNING,
  PRESCRIBE_CANCEL_ERROR,
  PRESCRIBE_CANCEL_SUCCESS,
} from 'CorePackage/MainModule/constants/notices';
import { CoreActionsEnum } from 'CorePackage/MainModule/enums/CoreActionsEnum';
import { LawInterfacesEnum } from 'CorePackage/MainModule/enums/LawInterfacesEnum';
import { IKnmMoveToPlanAttachment } from 'CorePackage/MainModule/interfaces/IKnmMoveToPlanAttachment';
import { getLawInterface } from 'CorePackage/MainModule/reducers/coreReducer';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import {
  ROUTE_ANALYTICS_KNM_ID,
  ROUTE_ANALYTICS_PM_ID,
  ROUTE_KNM,
  ROUTE_KNM_ID,
  ROUTE_PM,
  ROUTE_PM_ID,
} from 'CorePackage/MainModule/routes/routes';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { IExport } from 'APIPackage/ImportExportModule/interfaces/IExport';
import { notifyExportError, notifyExportSuccess } from 'CorePackage/MainModule/sagas/commonNotificationSagas';
import { IKnmStatus } from 'APIPackage/CatalogsModule/interfaces/IKnmStatus';
import { exportSimilaritiesInKnmList } from 'APIPackage/SimilarityModule/services/similaritiesApi';
import { deleteKnmSignById, downloadSignedZipById } from 'APIPackage/KnmModule/services/knmSignApi';
import { saveFile } from 'CorePackage/MainModule/common/saveFile';
import { REFRESH_DELAY } from 'CorePackage/MainModule/constants/refreshDelay';
import {
  fetchKnmAttachWebDocument,
  fetchKnmCopyById,
  fetchKnmToRevision,
  fetchKnmToApprove,
  fetchKnmRestoreById,
} from 'APIPackage/KnmModule/services/knmActionsApi';
import {
  fetchCheckKnoSupervisionLink,
  fetchExcludeDates,
  fetchIsKnmCanBeSigned,
  fetchInvalidFields,
  fetchKnmCreate,
  fetchKnmRestoreLatestSignedVersion,
  fetchKnmUpdateById,
  fetchOrders,
  fetchPlannedActivities,
  fetchUpdateAccessPrivilege,
  fetchKnmCheckChanges,
  fetchKnmSaveDraft,
  fetchKnmDraftDelete,
  fetchWorkingDaysCount,
  fetchKnmAgreementTemplateDownload,
  fetchErknmPrescribeCancel,
} from 'APIPackage/KnmModule/services/knmApi';
import {
  fetchDocDecisionByIdDoc,
  fetchDocDecisionByIdOdt,
  fetchDocWarningById,
  fetchKnmDownloadCurrent,
  fetchKnmExport,
  fetchKnmXmlDownloadById,
  generateExtract,
  generateExtractPm,
} from 'APIPackage/KnmModule/services/knmExportApi';
import {
  fetchKnmRemoveFromPlan,
  fetchErknmRemoveFromPlan,
  fetchKnmPlanFind,
  fetchGetNpaFromPlan,
  fetchGetPunishmentNature,
  fetchValidateUnsignedKnmByPlanId,
} from 'APIPackage/KnmModule/services/knmPlanApi';
import { KnmVersionsEnum } from 'APIPackage/KnmModule/enums/KnmVersionsEnum';
import { sagaWrapper } from 'CorePackage/MainModule/common/sagaWrapper';
import { addNoticeImportNotification } from 'CorePackage/NotificationModule/common/addNoticeImportNotification';
import { convertFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertFileToBase64';
import { IKnmAttachment } from 'APIPackage/KnmModule/interfaces/IKnmAttachment';
import {
  fetchKnmDocumentAttachmentUpload,
  fetchKnmDocumentSignaturesUpload,
} from 'APIPackage/KnmModule/services/knmDocumentAttachmentApi';
import { convertSignFileToBase64 } from 'KnmPackage/KnmDocumentsModule/common/convertSignFileToBase64';
import { formatDate, parseDate } from 'CorePackage/MainModule/common/formatDate';
import { SYSTEM_DATE_FORMAT_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { getPlanListPlan, getPlanListPlanErknmStatus } from 'PlanListPackage/MainModule/selectors/planListSelectors';
import { KnmSignatureInfoStatusesEnum } from 'APIPackage/KnmModule/enums/KnmSignatureInfoStatusesEnum';
import {
  getIsPlanErknmStatusConsidered,
  getIsPlanErknmStatusInProcess,
  getIsPlanErknmStatusUnderConsideration,
} from 'APIPackage/PlanListModule/common/getPlanErknmStatusFlags';
import { IErknmInfoExcludeDatesResponse } from 'KnmErknmPackage/ErknmInfoModule/interfaces/IErknmInfoExcludeDatesResponse';
import {
  getDecisionDataId,
  getDecisionKnoOrganizationFormValue,
  getDecisionSupervisionTypeFormValue,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { IValidatedUnsignedKnmByPlan } from 'APIPackage/KnmModule/interfaces/IValidatedUnsignedKnmByPlan';
import { convertAgreementsFormValuesToData } from 'KnmErknmPackage/MainModule/common/convertAgreementsFormValuesToData';
import { setDirtyActGuid } from 'CorePackage/HeaderModule/actions/headerActions';

import {
  knmSetSystemType,
  knmPlanFindGet,
  knmPlanFindGetRequest,
  knmPlanFindGetSuccess,
  knmPlanFindGetFailure,
  knmUpdate,
  knmUpdateRequest,
  knmUpdateSuccess,
  knmUpdateFailure,
  knmCopyRequest,
  knmCopySuccess,
  knmCopyFailure,
  knmSign,
  knmExcludeFromPlan,
  knmExcludeFromPlanRequest,
  knmExcludeFromPlanSuccess,
  knmExcludeFromPlanFailure,
  knmAttachWebDocument,
  knmExportKnm,
  knmChangeStatus,
  knmSetStatus,
  knmRemoveVisibleModal,
  knmExportSimilaritiesList,
  knmGetHistoryChanges,
  knmAddVisibleModal,
  knmCreate,
  knmInitEditorsUpdate,
  knmSetRawData,
  knmSetUrlId,
  knmHandleUploadErknmAttachWebDocument,
  knmCreatePlannedKnm,
  knmGet,
  knmSignSubmitFail,
  knmInfoGetExcludeDates,
  knmInfoSetExcludeDates,
  knmInfoSetPlannedActivities,
  knmInfoGetPlannedActivities,
  knmInfoGetNKOForeignAgentDataByOgrn,
  knmInfoSetNKOForeignAgentDataByOgrn,
  knmInfoGetIsOrgInSonkoRegistry,
  knmInfoSetIsOrgInSonkoRegistry,
  knmUpdateAccessPrivilege,
  knmInfoGetEpguNotificationList,
  knmInfoAddEpguNotificationList,
  knmInfoSetEpguNotificationTotalCount,
  knmInfoAddErvkObjectOfControl,
  knmInfoGetErvkObjectsOfControl,
  knmSignWithSaveSigner,
  knmTryGenerateDecision,
  knmPlanGetNpa,
  knmPlanGetNpaSuccess,
  knmPlanGetPunishmentNatureSuccess,
  knmGetOrders,
  knmSetOrders,
  knmReload,
  knmHistoryReload,
  validateUnsignedKnmByPlanIdPost,
  validateUnsignedKnmByPlanIdFailure,
  validateUnsignedKnmByPlanIdSuccess,
  validateUnsignedKnmByPlanIdRequest,
  knmSetIsCanBeSigned,
  knmGetInvalidFields,
  knmSetInvalidFields,
  knmClearInvalidFields,
  knmSetCheckKnoSupervisionLink,
  knmCheckChangesRequest,
  knmCheckChangesSuccess,
  knmCheckChangesFailure,
  knmErvkProsecutorGet,
  knmErvkProsecutorGetSuccess,
  knmErvkProsecutorGetFailure,
  knmErvkProsecutorGetRequest,
  knmGetKnoCollaboratingOrganisations,
  knmSetKnoCollaboratingOrganisations,
  knmGetKnoCollaboratingOrganisationsNextPage,
  knmTryOpenSignModal,
  knmInfoGetWorkDaysCountBeforeStart,
  knmInfoSetWorkDaysCountBeforeStart,
  knmDraftDelete,
  knmDraftSave,
  knmAgreementTemplateDownload,
  knmErknmCancelPrescribe,
  knmMakeChangeSubjectTypeAvailable,
  knmSetPrescribeCancelSigned,
  knmSetIsSaveOnProcess,
} from '../actions/knmActions';
import { KnmActionsEnum } from '../enums/KnmActionsEnum';
import { KnmSystemTypesEnum } from '../enums/KnmSystemTypesEnum';
import {
  getKnmData,
  getKnmPath,
  getKnmSystemType,
  getKnmDataId,
  getKnmModalIsVisible,
  getKnmFormRef,
  getKnmIsEditorsUpdateEnabled,
  getKnmOnSaveButtonClick,
  getKnmId,
  getEpguNotificationList,
  getEpguNotificationTotalCount,
  getKnmErknmKnoOrganizationDictVersionIdFormValue,
  getKnmErknmSupervisionTypeDictVersionIdFormValue,
  getKnmOrdersSelectConditionId,
  getKnmOrdersSelectOptions,
  getKnmErknmReasonsFormValue,
  getKnmTypeFormValue,
  getKnmCollaboratingOrganisationsPage,
  getKnmCollaboratingKnoOrganisations,
} from '../selectors/knmSelectors';
import { knmSignFunc, knmTestSignFunc } from '../common/knmSignFunc';
import { KnmModalsEnum } from '../enums/KnmModalsEnum';
import { init } from '../common/cryptoHelpers';
import { knmDeleteSagaFunc } from './knmDeleteSagaFunc';
import { watchKnmDictionariesSaga } from './knmDictionariesSaga';
import { fetchKnmSaga } from './knmFetchKnmSaga';
import { watchKnmBackgroundDataSaga } from './knmBackgroundDataSaga';
import { IKnmInfoPlannedActivities } from '../../../APIPackage/KnmModule/interfaces/IKnmInfoPlannedActivities';
import { fetchNKOForeignAgentByOgrn } from '../../../APIPackage/CatalogsModule/services/nkoForeignAgentApi';
import { INKOForeignAgent } from '../../../APIPackage/CatalogsModule/interfaces/INKOForeignAgent';
import { fetchIsOrgInSonkoRegistry } from '../../../APIPackage/CatalogsModule/services/sonkoRegistryApi';
import { fetchEpguNotification } from '../../../APIPackage/KnmModule/services/knmEpguNotificationApi';
import { IEpguNotificationPage } from '../../../APIPackage/KnmModule/interfaces/IEpguNotificationPage';
import { fetchErvkObjectsOfControl } from '../../../APIPackage/KnmModule/services/knmErvkObjectsOfControlApi';
import { IErvkObjectsOfControl } from '../../../APIPackage/KnmModule/interfaces/IErvkObjectsOfControl';
import { getDictionarySelectOptionWithState } from '../../../CorePackage/DictionariesModule/common/getDictionarySelectOptionWithState';
import { DictionaryUUIDsEnum } from '../../../CorePackage/DictionariesModule/enums/DictionaryUUIDsEnum';
import { convertKnmPmFormValuesToCreateParams } from '../../../KnmPmPackage/MainModule/common/convertKnmPmFormValuesToCreateParams';
import { mergeKnmParams } from '../common/mergeKnmParams';
import { knmFetchKnmSagaWithReturnKnm } from './knmFetchKnmSagaWithReturnKnm';
import { KnmPmActionsEnum } from '../../../KnmPmPackage/MainModule/enums/KnmPmActionsEnum';
import { knmTryGenerateWarning } from '../../../KnmPmPackage/MainModule/actions/knmPmActions';
import { IKnmDecisionNpaItem } from '../../../APIPackage/KnmModule/interfaces/IKnmDecisionNpaItem';
import { IKnmPunishmentNature } from '../../../APIPackage/KnmModule/interfaces/IKnmPunishmentNature';
import { IKnmParams } from '../../../APIPackage/KnmModule/interfaces/IKnmParams';
import { IKnmPlan } from '../../../PlanListPackage/MainModule/interfaces/IKnmPlan';
import { getPlanErknmStatusByStatusTitle } from '../../../APIPackage/PlanListModule/common/getPlanErknmStatusByStatusTitle';
import { fetchPlanById } from '../../../APIPackage/PlanListModule/services/knmPlanApi';
import { IKnmForm } from '../interfaces/IKnmForm';
import { KnmTypeCodesEnum } from '../../../APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { IKnmOrder } from '../../../APIPackage/KnmModule/interfaces/IKnmOrder';
import { IKnmOrderSelectOption } from '../interfaces/IKnmOrderSelectOption';
import { createKnmOrderSelectOption } from '../common/createKnmOrderSelectOption';
import { fetchKnmHistory } from '../../../APIPackage/KnmModule/services/knmAdditionalDataApi';
import { IErvkCorrectionRequiredData } from '../../../APIPackage/KnmModule/interfaces/IErvkCorrectionRequiredData';
import { fetchProsecutorOrganisationsByErvkUuidAndVersion } from '../../../APIPackage/CatalogsModule/services/prosecutorOrganisationsApi';
import { IUserOrganisation } from '../../../APIPackage/AccountModule/interfaces/IUserOrganisation';
import { IKnoOrganisationsChildrenResponse } from '../../../APIPackage/CatalogsModule/interfaces/IKnoOrganisationsChildrenResponse';
import {
  fetchKnoErvkOrganisationsChildrenWithKnoLevelCode,
  fetchKnoErvkOrganisationsSearch,
} from '../../../APIPackage/CatalogsModule/services/knoOrganisationsApi';
import { AUTOSAVE_DRAFT_VERSION } from '../constants/autosaveDraftVersion';
import { CREATE_DRAFT_EXCLUDED_FIELDS } from '../constants/createDraftExcludedFields';
import { filterKnmParamsByExcludedFields } from '../common/filterKnmParamsByExcludedFields';

export function* watchKnmSaga() {
  yield takeEvery(KnmActionsEnum.KNM_SIGN, signKnmSaga);
  yield takeEvery(KnmActionsEnum.KNM_SIGN_WITH_SAVE_SIGNER, signKnmWithSaveSignerSaga);
  yield takeEvery(KnmActionsEnum.KNM_TEST_SIGN, signTestKnmSaga);
  yield takeEvery(KnmActionsEnum.KNM_TEST_SIGN_WITH_SAVE_SIGNER, signTestKnmWithSaveSignerSaga);
  yield takeLatest(KnmActionsEnum.KNM_GET, knmGetHandlerSaga);
  yield takeLatest(KnmActionsEnum.KNM_CREATE, knmCreateSaga);
  yield takeLatest(KnmActionsEnum.KNM_UPDATE, fetchKnmUpdateSaga);
  yield takeLatest(KnmActionsEnum.KNM_DRAFT_SAVE, fetchKnmDraftSaveSaga);
  yield takeLatest(KnmActionsEnum.KNM_DRAFT_DELETE, fetchKnmDraftDeleteSaga);
  yield takeLatest(KnmActionsEnum.KNM_COPY, knmCopyHandlerSaga);
  yield takeLatest(KnmActionsEnum.KNM_EXCLUDE_FROM_PLAN, knmExcludeFromPlanHandlerSaga);
  yield takeEvery(KnmActionsEnum.KNM_DOWNLOAD_CURRENT_VERSION, knmDownloadCurrentVersionSaga);
  yield takeEvery(
    [CoreActionsEnum.CORE_SET_LAW_INTERFACE, KnmActionsEnum.KNM_SET_PATH, KnmActionsEnum.KNM_SET_RAW_DATA],
    updateKnmSystemType,
  );
  yield takeEvery(KnmActionsEnum.KNM_ATTACH_WEB_DOCUMENT, knmAttachWebDocumentSaga);
  yield takeLatest(KnmActionsEnum.KNM_PLAN_FIND_GET, fetchKnmPlanFindSaga);
  yield takeLatest(KnmActionsEnum.KNM_PLAN_VALIDATE_UNSIGNED_KNM, fetchValidateUnsignedKnmByPlanIdSaga);
  yield takeEvery(KnmActionsEnum.KNM_PLAN_GET_NPA, fetchGetNpaFromPlanSaga);
  yield takeEvery(KnmActionsEnum.KNM_PLAN_GET_PUNISHMENT_NATURE, fetchGetPunishmentNatureSaga);
  yield takeEvery(KnmActionsEnum.KNM_EXPORT_KNM, knmExportKnmSaga);
  yield takeEvery(KnmActionsEnum.KNM_CHANGE_STATUS, knmChangeStatusSaga);
  yield takeEvery(KnmActionsEnum.KNM_EXPORT_SIMILARITIES_LIST, knmExportSimilaritiesListSaga);
  yield takeEvery(KnmActionsEnum.KNM_TRY_OPEN_SIGN_MODAL, knmTryOpenSignModalSaga);
  yield takeEvery(KnmActionsEnum.KNM_TRY_GENERATE_DECISION, knmTryGenerateDecisionSaga);
  yield takeEvery(KnmPmActionsEnum.KNM_PM_TRY_GENERATE_WARNING, knmPmTryGenerateWarningSaga);
  yield takeEvery(KnmActionsEnum.KNM_DOWNLOAD_XML, knmDownloadXmlSaga);
  yield takeEvery(KnmActionsEnum.KNM_AGREEMENT_TEMPLATE_DOWNLOAD, knmDownloadAgreementTemplate);
  yield takeEvery(KnmActionsEnum.KNM_DELETE_SIGN, knmDeleteSignSaga);
  yield takeEvery(KnmActionsEnum.KNM_DOWNLOAD_SIGNED_ZIP, knmDownloadSignedZipSaga);
  yield takeEvery(KnmActionsEnum.KNM_RESTORE, knmRestoreSaga);
  yield takeEvery(KnmActionsEnum.KNM_RELOAD, knmReloadSaga);
  yield takeEvery(KnmActionsEnum.KNM_HISTORY_RELOAD, knmHistoryReloadSaga);
  yield takeEvery(KnmActionsEnum.KNM_DELETE, knmDeleteSaga);
  yield takeEvery(
    KnmActionsEnum.KNM_HANDLE_UPLOAD_ERKNM_ATTACH_WEB_DOCUMENT,
    knmHandleUploadErknmAttachWebDocumentSaga,
  );
  yield takeEvery(KnmActionsEnum.KNM_CREATE_PLANNED_KNM, knmCreatePlannedKnmSaga);
  yield takeEvery(KnmActionsEnum.KNM_SIGN_UPLOAD_MODAL_APPLY, knmSignUploadModalApplySaga);
  yield takeEvery(KnmActionsEnum.KNM_SIGN_SUBMIT_FAIL, knmSignSubmitFailSaga);
  yield takeEvery(KnmActionsEnum.KNM_INFO_GET_EXCLUDE_DATES, knmGetExcludeDatesSaga);
  yield takeEvery(KnmActionsEnum.KNM_INFO_GET_WORK_DAYS_COUNT_BEFORE_START, knmGetWorkDaysCountBeforeStart);
  yield takeEvery(KnmActionsEnum.KNM_GET_PLANNED_ACTIVITIES, knmGetPlannedActivitiesSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_NKO_FOREIGN_AGENT_DATA_BY_OGRN, knmInfoGetNKOForeignAgentDataByOgrnSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_IS_ORG_IN_SONKO_REGISTRY, knmInfoGetIsOrgInSonkoRegistrySaga);
  yield takeEvery(KnmActionsEnum.KNM_UPDATE_ACCESS_PRIVILEGE, knmUpdateAccessPrivilegeSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_EPGU_NOTIFICATION_LIST, knmInfoGetEpguNotificationListSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_ERVK_OBJECTS_OF_CONTROL, knmInfoGetErvkObjectsOfControlSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_ORDERS, knmGetOrdersSaga);
  yield takeEvery(KnmActionsEnum.KNM_CHECK_CHANGES, knmCheckChangesSaga);

  yield takeEvery([KnmActionsEnum.KNM_GET_SUCCESS, KnmActionsEnum.KNM_UPDATE_SUCCESS], getIsKnmCanBeSignedSaga);
  yield takeEvery(KnmActionsEnum.KNM_CHECK_KNO_SUPERVISION_LINKS, checkKnoSupervisionLinkSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_INVALID_FIELDS, knmGetInvalidFieldsSaga);
  yield takeEvery(KnmActionsEnum.KNM_ERVK_PROSECUTOR_GET, knmGetProsecutorOrganisationsSaga);
  yield takeEvery(KnmActionsEnum.KNM_GET_KNO_COLLABORATING_ORGANISATIONS, knmGetKnoCollaboratingOrganisationsSaga);
  yield takeEvery(
    KnmActionsEnum.KNM_GET_KNO_COLLABORATING_ORGANISATIONS_NEXT_PAGE,
    knmGetKnoCollaboratingOrganisationsNextPageSaga,
  );
  yield takeEvery(KnmActionsEnum.KNM_SEND_AGREEMENT, knmSendAgreementSaga);
  yield takeEvery(KnmActionsEnum.KNM_RESTORE_LATEST_SIGNED_VERSION, knmRestoreLatestSignedVersion);
  yield takeEvery(KnmActionsEnum.KNM_ERKNM_CANCEL_PRESCRIBE, KnmErknmCancelPrescribeSaga);
  yield all([watchKnmDictionariesSaga(), watchKnmBackgroundDataSaga()]);
}

function* knmReloadSaga(action?: ActionType<typeof knmReload>) {
  function* funcTry() {
    const callback = action?.payload.callback;
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (knmId) {
      yield fetchKnmSaga(knmId);
      if (callback) {
        callback();
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* knmHistoryReloadSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (knmId) {
      yield fetchKnmHistory(knmId);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* knmAttachWebDocumentSaga(action: ActionType<typeof knmAttachWebDocument>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (knmId) {
      yield delay(REFRESH_DELAY);
      yield fetchKnmAttachWebDocument(knmId, data);
      yield delay(REFRESH_DELAY);
      yield fetchKnmSaga(knmId);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка сохранения документа для надзора',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* signKnmWithSaveSignerSaga(action: ActionType<typeof knmSignWithSaveSigner>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const isPm = !!getKnmData(state)?.pmErknm;
    const knmId = action.payload.id ?? getKnmDataId(state);
    const knmSystemType = getKnmSystemType(state);
    const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const { certificate, signer, actGuid, prescriptionGuid, isCancelSign } = action.payload;

    if (knmId) {
      yield put(knmSetPrescribeCancelSigned(false));
      yield knmSignFunc(certificate, isErknm, knmId, actGuid, prescriptionGuid, undefined, isPm, isCancelSign);
      yield delay(REFRESH_DELAY);

      let knmData: IKnm | undefined;

      yield knmFetchKnmSagaWithReturnKnm(knmId, data => {
        knmData = data;
      });

      const formRef = getKnmFormRef(state);

      if (knmData && knmData.pmErknm) {
        knmData.pmErknm.officialFullName = signer?.fullName || '';

        const positionSigner = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          signer?.position?.dictId,
          signer?.position?.dictVersionId,
        );

        knmData.pmErknm.warningContentCreatorPosition = positionSigner?.title || '';

        const valueForm = formRef?.current.getState().values;

        if (valueForm) {
          yield fetchKnmUpdateById(convertKnmPmFormValuesToCreateParams(valueForm, knmData, true));
        }
      }

      yield fetchKnmSaga(knmId);

      if (actGuid || prescriptionGuid) {
        let organizationGuid = '';

        if (actGuid) {
          if (isPm) {
            organizationGuid = knmData?.pmErknm?.subject?.guid || '';
          } else {
            organizationGuid = knmData?.knmErknm?.organizations?.find(i => i.act?.guid === actGuid)?.guid || '';
          }
        } else if (isPm) {
          organizationGuid = knmData?.pmErknm?.subject?.guid || '';
        } else {
          organizationGuid =
            knmData?.knmErknm?.organizations?.find(i => !!i.resultDecisions?.find(rd => rd.guid === prescriptionGuid))
              ?.guid || '';
        }
        yield put(knmInfoGetEpguNotificationList(organizationGuid, 'KNM', true));
      }

      yield put(knmSetPrescribeCancelSigned(!!isCancelSign));
      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SIGN_ADD_SUCCESS : KNM_SIGN_ADD_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    }
  }
  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const message =
        err.clientErrorCode === 'E4101'
          ? err.message
          : 'Паспорт ПМ не подписан. Необходимо обратиться в службу технической поддержки»: процесс подписания завершился с различными ошибками системы.';

      yield put(
        addNoticeAction({
          renderContent: message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    },
  });
}

function* signTestKnmWithSaveSignerSaga(action: ActionType<typeof knmSignWithSaveSigner>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = action.payload.id ?? getKnmDataId(state);
    const knmSystemType = getKnmSystemType(state);
    const { signer } = action.payload;

    if (knmId) {
      yield knmTestSignFunc(knmId);
      yield delay(REFRESH_DELAY);

      let knmData: IKnm | undefined;

      yield knmFetchKnmSagaWithReturnKnm(knmId, data => {
        knmData = data;
      });

      const formRef = getKnmFormRef(state);

      if (knmData && knmData.pmErknm) {
        knmData.pmErknm.officialFullName = signer?.fullName || '';

        const positionSigner = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
          state,
          signer?.position?.dictId,
          signer?.position?.dictVersionId,
        );

        knmData.pmErknm.warningContentCreatorPosition = positionSigner?.title || '';

        const valueForm = formRef?.current.getState().values;

        if (valueForm) {
          yield fetchKnmUpdateById(convertKnmPmFormValuesToCreateParams(valueForm, knmData, true));
        }
      }

      yield fetchKnmSaga(knmId);
      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SIGN_ADD_SUCCESS : KNM_SIGN_ADD_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const message =
        err.clientErrorCode === 'E4101'
          ? err.message
          : 'Паспорт КНМ не подписан. Необходимо обратиться в службу технической поддержки: процесс подписания завершился с различными ошибками системы.';

      yield put(
        addNoticeAction({
          renderContent: message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    },
  });
}

function* signKnmSaga(action: ActionType<typeof knmSign>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = action.payload.id ?? getKnmDataId(state);
    const knmData = getKnmData(state);
    const isPm = !!getKnmData(state)?.pmErknm;
    const knmSystemType = getKnmSystemType(state);
    const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const { certificate, actGuid, signer, prescriptionGuid, isCancelSign } = action.payload;

    if (knmId) {
      yield put(knmSetPrescribeCancelSigned(false));
      yield knmSignFunc(certificate, isErknm, knmId, actGuid, prescriptionGuid, undefined, isPm, isCancelSign);
      yield delay(REFRESH_DELAY);
      if (!isErknm && isPm && knmData?.version === KnmVersionsEnum.ERP) {
        let tempKnmData: IKnm | undefined;

        yield knmFetchKnmSagaWithReturnKnm(knmId, data => {
          tempKnmData = data;
        });

        if (tempKnmData && tempKnmData.pmErknm) {
          // нужна задержка, чтобы успели просетится значения в redux,
          // если убрать то для пм в ерп станет нестабильно происходить подписание (в частности акты)
          // fetchKnmUpdateById нужен тк подписание в ПМ в ерп прокинуто через еркнм-бэкенд
          // и для корректной работы после прокидки через еркнм бекенд нужно дополнительно сохранять через ерп
          // TODO: сделать нормальное подписание в ерп или прокинуть дополнительное сохранение через ерп бекенд из еркнм бека
          // или со стороны фронта удостоверится что поля подписания успели засетится в стейт
          yield delay(REFRESH_DELAY * 0.25);

          tempKnmData.pmErknm.officialFullName = signer?.fullName || '';
          const positionSigner = getDictionarySelectOptionWithState<typeof DictionaryUUIDsEnum.PARTICIPANT_POSITIONS>(
            state,
            signer?.position?.dictId,
            signer?.position?.dictVersionId,
          );

          tempKnmData.pmErknm.warningContentCreatorPosition = positionSigner?.title || '';

          const nextState: IRootState = yield select();
          const valueForm = nextState.knmPm.initialFormValues;

          if (valueForm) {
            // мы только что получили с бекенда полностью подписанный пасспорт поэтому ненужно сбрасывать подпись с акта
            yield put(setDirtyActGuid(undefined));
            yield fetchKnmUpdateById(convertKnmPmFormValuesToCreateParams(valueForm, tempKnmData, false));
          }
        }
      }

      yield fetchKnmSaga(knmId);
      if (actGuid || prescriptionGuid) {
        let organizationGuid = '';

        if (actGuid) {
          if (isPm) {
            organizationGuid = knmData?.pmErknm?.subject?.guid || '';
          } else {
            organizationGuid = knmData?.knmErknm?.organizations?.find(i => i.act?.guid === actGuid)?.guid || '';
          }
        } else if (isPm) {
          organizationGuid = knmData?.pmErknm?.subject?.guid || '';
        } else {
          organizationGuid =
            knmData?.knmErknm?.organizations?.find(i => !!i.resultDecisions?.find(rd => rd.guid === prescriptionGuid))
              ?.guid || '';
        }

        yield put(knmInfoGetEpguNotificationList(organizationGuid, 'KNM', true));
      }
      yield put(knmSetPrescribeCancelSigned(!!isCancelSign));
      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SIGN_ADD_SUCCESS : KNM_SIGN_ADD_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const message =
        err.clientErrorCode === 'E4101'
          ? err.message
          : 'Паспорт КНМ не подписан. Необходимо обратиться в службу технической поддержки: процесс подписания завершился с различными ошибками системы.';

      yield put(
        addNoticeAction({
          renderContent: message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    },
    *funcFinally() {
      yield put(knmMakeChangeSubjectTypeAvailable(false));
    },
  });
}

function* signTestKnmSaga(action: ActionType<typeof knmSign>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = action.payload.id ?? getKnmDataId(state);
    const knmSystemType = getKnmSystemType(state);

    if (knmId) {
      yield knmTestSignFunc(knmId);
      yield delay(REFRESH_DELAY);
      yield fetchKnmSaga(knmId);
      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SIGN_ADD_SUCCESS : KNM_SIGN_ADD_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      const message =
        err.clientErrorCode === 'E4101'
          ? err.message
          : 'Паспорт КНМ не подписан. Необходимо обратиться в службу технической поддержки: процесс подписания завершился с различными ошибками системы.';

      yield put(
        addNoticeAction({
          renderContent: message,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PASSPORT_SIGN,
        }),
      );
    },
  });
}

function* knmGetHandlerSaga(action: ActionType<typeof knmGet>) {
  const { onSuccess } = action.payload;

  const state: IRootState = yield select();
  const knmId = getKnmDataId(state);

  if (typeof knmId === 'number') {
    yield fetchKnmSaga(knmId, onSuccess);
  }
}

function* fetchKnmUpdateSaga(action: ActionType<typeof knmUpdate>) {
  const { data } = action.payload;

  function* funcTry() {
    yield put(knmUpdateRequest());
    const state: IRootState = yield select();
    const knmSystemType = getKnmSystemType(state);
    const formRef = getKnmFormRef(state);

    let errorText: string | undefined;
    const isPlannedErknm = data?.type?.code === KnmTypeCodesEnum.PP && knmSystemType === KnmSystemTypesEnum.ERKNM;
    const isPlanIdChanged = formRef?.current.getState().dirtyFields['planId' as keyof IKnmForm];

    if (isPlannedErknm && data.planId !== null && isPlanIdChanged) {
      const plan: IKnmPlan = yield fetchPlanById(data.planId);

      errorText = isValidPlannedKnm(data, plan);
    }

    if (!errorText) {
      const responseData: IKnm = yield fetchKnmUpdateById(data);

      yield put(knmUpdateSuccess(responseData));
      yield put(
        addNoticeAction({
          renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SAVE_SUCCESS : KNM_SAVE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    } else {
      yield put(
        addNoticeAction({
          renderContent: errorText,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      const errorMessage = error.message;

      yield put(knmUpdateFailure());
      if (errorMessage?.includes(HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS)) {
        yield put(
          addNoticeAction({
            renderContent: HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (errorMessage?.includes(PLANNED_KNM_CREATED_LATE)) {
        yield put(
          addNoticeAction({
            renderContent: NOTICE_PLANNED_KNM_CREATED_LATE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (errorMessage?.includes(HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS)) {
        yield put(
          addNoticeAction({
            renderContent: NOTICE_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (errorMessage?.includes(KNM_RESULT_FILLED_EARLY)) {
        yield put(
          addNoticeAction({
            renderContent: KNM_RESULT_FILLED_EARLY_ERROR_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (errorMessage?.includes(KNM_NOT_AVAILABLE_FOR_CHANGE)) {
        yield put(
          addNoticeAction({
            renderContent: KNM_NOT_AVAILABLE_FOR_CHANGE_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (errorMessage?.includes(LINKED_KNM_ERROR)) {
        yield put(
          addNoticeAction({
            renderContent: KNM_LINK_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.KNM_LINK,
          }),
        );
      } else if (errorMessage?.includes(DUPLICATE_KEY_REASON_TYPE)) {
        yield put(
          addNoticeAction({
            renderContent: DUPLICATE_KEY_REASON_TYPE_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else if (errorMessage?.includes(KNM_OUT_OF_MEMORY) || errorMessage?.includes(KNM_OUT_OF_MEMORY_KAFKA)) {
        yield put(
          addNoticeAction({
            renderContent: KNM_OUT_OF_MEMORY_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else if (errorMessage?.includes(SEVERAL_REASONS_ARE_FORBIDDEN)) {
        yield put(
          addNoticeAction({
            renderContent: SEVERAL_REASONS_ARE_FORBIDDEN_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            renderContent: errorMessage,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      }
    },
  });
}

function* fetchKnmDraftSaveSaga(action: ActionType<typeof knmUpdate>) {
  const { data } = action.payload;

  function* funcTry() {
    const state: IRootState = yield select();
    const knmData: IKnm = yield fetchKnmSaveDraft({ ...data, draftVersion: AUTOSAVE_DRAFT_VERSION });
    const formRef = getKnmFormRef(state);

    if (formRef?.current) {
      formRef?.current.change('draft' as keyof IKnmForm, knmData.draft);
      formRef?.current.change('draftSavedAt' as keyof IKnmForm, knmData.draftSavedAt);
    }
  }

  yield funcTry();
}

function* fetchKnmDraftDeleteSaga(action: ActionType<typeof knmDraftDelete>) {
  const { id, callback, shouldReload } = action.payload;

  function* funcTry() {
    yield fetchKnmDraftDelete(id);
    callback && callback();

    if (shouldReload) {
      yield put(knmGet());
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* fetchKnmCopySaga(knmId: number) {
  function* funcTry() {
    yield put(knmCopyRequest());
    const state: IRootState = yield select();
    const knmSystemType = getKnmSystemType(state);
    const data: IKnm = yield fetchKnmCopyById(knmId);

    yield put(knmCopySuccess(data));
    window.history.pushState(null, '', `${ROUTE_KNM(data.id)}${window.location.hash}`);
    yield put(
      addNoticeAction({
        renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_COPY_SUCCESS : KNM_COPY_SUCCESS,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.COPY_KNM,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(error: IResponseError) {
      yield put(knmCopyFailure());
      yield put(
        addNoticeAction({
          renderContent: error.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.COPY_KNM,
        }),
      );
    },
  });
}

function* knmCopyHandlerSaga() {
  const state: IRootState = yield select();
  const knmId = getKnmDataId(state);

  if (typeof knmId === 'number') {
    yield fetchKnmCopySaga(knmId);
  }
}

function* fetchKnmExcludeFromPlanSaga(
  knmId: number,
  data?: IKnmMoveToPlanAttachment | IKnmExclusionReasonSelectOption,
) {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmSystemType = getKnmSystemType(state);
    const isErpKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERP;
    const isErknmKnmSystemType = knmSystemType === KnmSystemTypesEnum.ERKNM;

    yield put(knmExcludeFromPlanRequest());

    if (isErpKnmSystemType) {
      yield fetchKnmRemoveFromPlan({
        knmIds: [knmId],
        exclusionReason: deleteObjectProperties(data as IKnmExclusionReasonSelectOption, ['label', 'value']),
      });
    }
    if (isErknmKnmSystemType) {
      const { reason, document, signatureInfo, type, linkedPmId } = data as IKnmMoveToPlanAttachment;

      yield fetchErknmRemoveFromPlan(
        [knmId.toString()],
        reason,
        {
          active: null,
          attachments: [
            {
              ...document,
              signatureInfo,
              signed: true,
            },
          ],
          correlationToken: null,
          description: null,
          guid: null,
          type,
        },
        linkedPmId,
      );
    }
    yield put(knmExcludeFromPlanSuccess());
    yield put(
      addNoticeAction({
        renderContent: KNM_UNLIST_SUCCESSFULLY,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.UNLIST_KNM,
      }),
    );
    yield fetchKnmSaga(knmId);
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmExcludeFromPlanFailure());
      yield put(
        addNoticeAction({
          renderContent: KNM_UNLIST_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.UNLIST_KNM,
        }),
      );
    },
  });
}

function* knmExcludeFromPlanHandlerSaga(action: ActionType<typeof knmExcludeFromPlan>) {
  const { data } = action.payload;
  const state: IRootState = yield select();
  const knmId = getKnmDataId(state);

  if (typeof knmId === 'number') {
    yield fetchKnmExcludeFromPlanSaga(knmId, data);
  }
}

function* knmDownloadCurrentVersionSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (typeof knmId === 'number') {
      const response: IExport = yield fetchKnmDownloadCurrent(knmId);

      yield notifyExportSuccess(response.id);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      if (currentError.message?.includes(SNAPSHOT_NOT_FOUND)) {
        yield put(
          addNoticeAction({
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.FILE_UPLOAD,
            renderContent: 'Ранее сохраненная подпись не найдена',
          }),
        );
      } else {
        yield notifyExportError();
      }
    },
  });
}

function* updateKnmSystemType() {
  const state: IRootState = yield select();
  const lawInterface = getLawInterface(state);
  const knmData = getKnmData(state);
  const path = getKnmPath(state);
  let systemType: KnmSystemTypesEnum | undefined;

  if (knmData) {
    if (knmData.knmErknm && knmData.version !== KnmVersionsEnum.ERP) {
      systemType = KnmSystemTypesEnum.ERKNM;
    } else if (knmData.pmErknm) {
      systemType = KnmSystemTypesEnum.PM;
    } else {
      systemType = KnmSystemTypesEnum.ERP;
    }
  } else if (path === ROUTE_KNM_ID || path === ROUTE_ANALYTICS_KNM_ID) {
    if (lawInterface === LawInterfacesEnum.FZ_248) {
      systemType = KnmSystemTypesEnum.ERKNM;
    } else {
      systemType = KnmSystemTypesEnum.ERP;
    }
  } else if (path === ROUTE_PM_ID || path === ROUTE_ANALYTICS_PM_ID) {
    systemType = KnmSystemTypesEnum.PM;
  }
  yield put(knmSetSystemType(systemType));
}

function* fetchKnmPlanFindSaga(action: ActionType<typeof knmPlanFindGet>) {
  function* funcTry() {
    const { planId } = action.payload;

    yield put(knmPlanFindGetRequest());
    const knmPlanFind: IKnmPlanFind = yield fetchKnmPlanFind(planId);

    yield put(knmPlanFindGetSuccess(knmPlanFind));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmPlanFindGetFailure());
    },
  });
}

function* fetchValidateUnsignedKnmByPlanIdSaga(action: ActionType<typeof validateUnsignedKnmByPlanIdPost>) {
  function* funcTry() {
    const { planId, fioSigner, fioSignerCN, organizationName } = action.payload;

    yield put(validateUnsignedKnmByPlanIdRequest());
    const validationResults: IValidatedUnsignedKnmByPlan = yield fetchValidateUnsignedKnmByPlanId(
      planId,
      fioSigner,
      fioSignerCN,
      organizationName,
    );

    yield put(validateUnsignedKnmByPlanIdSuccess(validationResults));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(validateUnsignedKnmByPlanIdFailure());
    },
  });
}

function* fetchGetNpaFromPlanSaga(action: ActionType<typeof knmPlanGetNpa>) {
  function* funcTry() {
    const { dictVersionId } = action.payload;

    const npa: IKnmDecisionNpaItem[] = yield fetchGetNpaFromPlan(dictVersionId);

    yield put(knmPlanGetNpaSuccess(npa));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmPlanFindGetFailure());
    },
  });
}

function* fetchGetPunishmentNatureSaga() {
  function* funcTry() {
    const values: IKnmPunishmentNature[] = yield fetchGetPunishmentNature();

    yield put(knmPlanGetPunishmentNatureSuccess(values));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmPlanFindGetFailure());
    },
  });
}

function* knmExportKnmSaga(action: ActionType<typeof knmExportKnm>) {
  function* funcTry() {
    const { format } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      const response: IExport = yield fetchKnmExport({ id, format });

      yield put(addNoticeImportNotification(response.id));
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.EXPORT_KNM,
        }),
      );
    },
  });
}

function* knmChangeStatusSaga(action: ActionType<typeof knmChangeStatus>) {
  function* funcTry() {
    const { commentText } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmDataId(state);
    const isRevision = getKnmModalIsVisible([KnmModalsEnum.CHANGE_STATUS_MODAL_TO_REVISION])(state);
    const isApprove = getKnmModalIsVisible([KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE])(state);

    if (id) {
      let response: IKnmStatus | null = null;

      if (isRevision) {
        response = yield fetchKnmToRevision(id, commentText);
      }
      if (isApprove) {
        response = yield fetchKnmToApprove(id, commentText);
      }
      if (response) {
        yield put(knmSetStatus(response));
      }
      yield delay(REFRESH_DELAY);
      yield fetchKnmSaga(id);
      yield put(knmGetHistoryChanges());
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcFinally() {
      yield put(knmRemoveVisibleModal(KnmModalsEnum.CHANGE_STATUS_MODAL_TO_APPROVE));
      yield put(knmRemoveVisibleModal(KnmModalsEnum.CHANGE_STATUS_MODAL_TO_REVISION));
    },
  });
}

function* knmExportSimilaritiesListSaga(action: ActionType<typeof knmExportSimilaritiesList>) {
  function* funcTry() {
    const { format, type } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      const response: IExport = yield exportSimilaritiesInKnmList(String(id), format, type);

      yield put(addNoticeImportNotification(response.id));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmTryOpenSignModalSaga(action: ActionType<typeof knmTryOpenSignModal>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;
    const formRef = getKnmFormRef(state);
    const {
      actGuid,
      prescriptionGuid,
      isNotNeedSubmitForm,
      onClickButtonSignBeforeInnerAction,
      abortCheckCertificate,
      onSuccessCheckCertificate,
      isSignAfterSuccessCheckCertificateAction,
      isNeedSubjectFioCheck,
      signerSubjectFioForCheck,
    } = action.payload;

    yield init();
    yield put(
      addNoticeAction({
        renderContent: isPm ? CHECK_PM_BEFORE_SIGN_WARNING : CHECK_KNM_BEFORE_SIGN_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
      }),
    );
    if (formRef?.current) {
      formRef?.current.change('shouldValidationForGeneratedDecision', false);

      if (!isNotNeedSubmitForm) {
        // TODO: проверить нужен ли setCanSubmit(false);
        formRef.current.submit();
        // TODO: проверить нужен ли setCanSubmit(false);
      }

      const { dirty: isDirty, hasValidationErrors: isValidationErrors, dirtyFields } = formRef.current.getState();

      // для актов делаем глубокое сравнение полей на предмет изменения (да, костыль, но по другому никак в текущей реализации)
      const isDirtyForm = () => {
        if (!actGuid) {
          return isDirty;
        }
        if (!dirtyFields || typeof dirtyFields !== 'object') {
          return false;
        }
        const actKeys = Object.keys(dirtyFields).filter(key => key.includes('acts'));

        if (actKeys.length === 0) {
          return false;
        }

        type FormWithGetFieldState = {
          getFieldState: (field: string) => { initial?: unknown; value?: unknown } | undefined;
        };
        const form = formRef?.current as FormWithGetFieldState | null | undefined;

        return actKeys.some(key => {
          const formState = form?.getFieldState(key);
          const initial = formState?.initial;
          const fieldValue = formState?.value;

          return !_.isEqual(initial, fieldValue);
        });
      };

      if (isValidationErrors) {
        yield put(
          addNoticeAction({
            renderContent: isPm ? CHECK_PM_BEFORE_SIGN_ERROR : CHECK_KNM_BEFORE_SIGN_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
          }),
        );
      } else if (isDirtyForm()) {
        yield put(
          addNoticeAction({
            renderContent: isPm ? CHECK_PM_BEFORE_SIGN_NOT_SAVED_WARNING : CHECK_KNM_BEFORE_SIGN_NOT_SAVED_WARNING,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN_NOT_SAVED,
          }),
        );
      } else {
        yield put(
          knmAddVisibleModal(KnmModalsEnum.SIGN_MODAL, {
            actGuid,
            prescriptionGuid,
            onClickButtonSignBeforeInnerAction,
            abortCheckCertificate,
            onSuccessCheckCertificate,
            isSignAfterSuccessCheckCertificateAction,
            isNeedSubjectFioCheck,
            signerSubjectFioForCheck,
          }),
        );
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(err: IResponseError) {
      // eslint-disable-next-line no-console
      console.error('ОШИБКА:', err);

      yield put(
        addNoticeAction({
          renderContent: CRIPTOPRO_NOT_EXISTS_ERROR,
          noticeType: NoticeTypesEnum.WARNING,
          messageType: MessageTypesEnum.CRIPTOPRO_NOT_EXISTS,
        }),
      );
    },
  });
}

function* knmTryGenerateDecisionSaga(action: ActionType<typeof knmTryGenerateDecision>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const formRef = getKnmFormRef(state);
    const { id, isDoc } = action.payload;

    if (formRef?.current) {
      formRef?.current.change('shouldValidationForGeneratedDecision', true);

      formRef.current.submit();
      const { dirty: isDirty, hasValidationErrors: isValidationErrors } = formRef.current.getState();

      const valuesForm = formRef?.current.getState().values;
      const isDecisionDisabled = valuesForm?.decisionDisabled;

      if (isValidationErrors) {
        yield put(
          addNoticeAction({
            renderContent: isDecisionDisabled ? CHECK_GENERATE_EXTRACT : CHECK_GENERATE_DECISION,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
          }),
        );
      } else if (isDirty) {
        yield put(
          addNoticeAction({
            renderContent: CHECK_GENERATE_DECISION_NOT_SAVED_WARNING,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN_NOT_SAVED,
          }),
        );
      } else {
        let response: Blob | undefined;

        if (isDecisionDisabled) {
          yield generateExtract(id, isDoc ? 'docx' : 'odt');
          yield put(knmReload());
          yield put(knmHistoryReload());
        } else {
          response = isDoc ? yield fetchDocDecisionByIdDoc(id) : yield fetchDocDecisionByIdOdt(id);

          if (response) {
            const file = new Blob([response]);

            saveFile(file, `${id}.${isDoc ? 'docx' : 'odt'}`);
          }
        }

        formRef.current.change('shouldValidationForGeneratedDecision', false);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmPmTryGenerateWarningSaga(action: ActionType<typeof knmTryGenerateWarning>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const formRef = getKnmFormRef(state);
    const { id, isStatement } = action.payload;

    if (formRef?.current) {
      formRef?.current.change('shouldValidationForGeneratedWarning', true);

      formRef.current.submit();
      const { dirty: isDirty, hasValidationErrors: isValidationErrors } = formRef.current.getState();

      if (isValidationErrors) {
        yield put(
          addNoticeAction({
            renderContent: isStatement ? CHECK_GENERATE_STATEMENT_WARNING : CHECK_GENERATE_WARNING,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN,
          }),
        );
      } else if (isDirty) {
        yield put(
          addNoticeAction({
            renderContent: CHECK_GENERATE_WARNING_NOT_SAVED_WARNING,
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.CHECK_KNM_BEFORE_SIGN_NOT_SAVED,
          }),
        );
      } else {
        if (isStatement) {
          yield generateExtractPm(id);
          yield put(knmHistoryReload());
          yield put(knmReload());
        } else {
          const response: Blob = yield fetchDocWarningById(id);
          const file = new Blob([response]);

          saveFile(file, `${id}.docx`);
        }

        formRef.current.change('shouldValidationForGeneratedWarning', false);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmDownloadXmlSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      const response: Blob = yield fetchKnmXmlDownloadById(id);
      const file = new Blob([response]);

      saveFile(file, `${id}.xml`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DOWNLOAD_XML,
        }),
      );
    },
  });
}

function* knmDeleteSignSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      yield deleteKnmSignById(id);
      yield delay(REFRESH_DELAY);
      yield fetchKnmSaga(id);
      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent: KNM_SIGN_DELETE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.DELETE_KNM_SIGN,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_KNM_SIGN,
        }),
      );
    },
  });
}

function* knmDownloadSignedZipSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      const response: Blob = yield downloadSignedZipById(id);
      const file = new Blob([response]);

      saveFile(file, `${id}.zip`);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DOWNLOAD_KNM_ZIP,
        }),
      );
    },
  });
}

function* knmRestoreSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);
    const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

    if (id) {
      yield fetchKnmRestoreById(id);
      yield delay(REFRESH_DELAY);
      yield fetchKnmSaga(id);
      yield put(
        addNoticeAction({
          renderContent: isPm ? PM_RESTORE_SUCCESS : KNM_RESTORE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.RESTORE_KNM,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.RESTORE_KNM,
        }),
      );
    },
  });
}

function* knmDeleteSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const id = getKnmDataId(state);
    const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

    if (id) {
      yield knmDeleteSagaFunc([id], isPm);
      yield fetchKnmSaga(id);
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DELETE_KNM,
        }),
      );
    },
  });
}

function* knmCreateSaga(action: ActionType<typeof knmCreate>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const isKnmEditorsUpdateEnabled = getKnmIsEditorsUpdateEnabled(state);

    const isPm: boolean = getKnmSystemType(state) === KnmSystemTypesEnum.PM;

    // Фильтруем поля для создания (например потенциально невалидные)
    // В теории можно генерировать CREATE_DRAFT_EXCLUDED_FIELDS на основе initialInvalidFields, но сейчас это выглядит избыточно
    const { filtered: filteredDataForCreate, hasChanges } = isPm
      ? filterKnmParamsByExcludedFields(data, CREATE_DRAFT_EXCLUDED_FIELDS)
      : { filtered: data, hasChanges: false };
    const response: IKnm = yield fetchKnmCreate({ data: filteredDataForCreate });

    if (isKnmEditorsUpdateEnabled) {
      yield put(knmInitEditorsUpdate());
    }
    yield put(knmSetRawData(response));
    yield put(knmSetIsSaveOnProcess(false));

    if (response) {
      // Если не было исключенных полей, то и черновик нам не нужен
      // пока этот функционал для кейса создания копии ПМ с невалидыми данными
      if (response?.pmErknm && hasChanges) {
        const formRef = getKnmFormRef(state);
        const valueForm = formRef?.current.getState().values;

        if (valueForm) {
          const draftParams = convertKnmPmFormValuesToCreateParams(valueForm, response, false, true);

          if (draftParams) {
            // Сохраняем черновик со всеми исключенными параметрами
            const mergedWithExcluded = mergeKnmParams(response, draftParams);

            yield put(knmDraftSave(mergedWithExcluded));
          }
        }
      }

      window.history.pushState(
        null,
        '',
        `${isPm ? ROUTE_PM(response.id) : ROUTE_KNM(response.id, window.location.search)}${window.location.hash}`,
      );
      yield put(knmSetUrlId(response.id.toString()));

      yield put(knmGetHistoryChanges());
      yield put(
        addNoticeAction({
          renderContent:
            response.version === KnmVersionsEnum.ERKNM && response.pmErknm ? PM_SAVE_SUCCESS : KNM_SAVE_SUCCESS,
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.SAVE_KNM,
        }),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(createKnmError: IResponseError) {
      yield put(knmSetIsSaveOnProcess(false));

      if (createKnmError.message?.includes(HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS)) {
        yield put(
          addNoticeAction({
            renderContent: KNM_NOT_AVAILABLE_LINK_TO_PLAN_KNO_OR_PROSECUTOR_NOT_EQUALS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (
        createKnmError.message?.includes(HTTP_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS)
      ) {
        yield put(
          addNoticeAction({
            renderContent: NOTICE_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (createKnmError.message?.includes(PLANNED_KNM_CREATED_LATE)) {
        yield put(
          addNoticeAction({
            renderContent: NOTICE_PLANNED_KNM_CREATED_LATE,

            noticeType: NoticeTypesEnum.ERROR,

            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (createKnmError.message?.includes('KNM_NOT_AVAILABLE_LINK_TO_PLAN')) {
        yield put(
          addNoticeAction({
            renderContent: KNM_NOT_AVAILABLE_LINK_TO_PLAN_LESS_30_DAYS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (createKnmError.message?.includes(INN_NOT_MATCH_OGRN)) {
        yield put(
          addNoticeAction({
            renderContent: INN_NOT_MATCH_OGRN,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else if (createKnmError.message?.includes('PLANNED_KNM_CREATED_LATE')) {
        yield put(
          addNoticeAction({
            renderContent: PLANNED_KNM_CREATED_LATE_LESS_30_DAYS,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
          }),
        );
      } else if (createKnmError.message?.includes(DUPLICATE_KEY_REASON_TYPE)) {
        yield put(
          addNoticeAction({
            renderContent: DUPLICATE_KEY_REASON_TYPE_ERROR,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else if (createKnmError.message?.includes(SEVERAL_REASONS_ARE_FORBIDDEN)) {
        yield put(
          addNoticeAction({
            renderContent: SEVERAL_REASONS_ARE_FORBIDDEN_MESSAGE,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            renderContent: createKnmError.message,
            noticeType: NoticeTypesEnum.ERROR,
            messageType: MessageTypesEnum.SAVE_KNM,
          }),
        );
      }
    },
  });
}

function* knmHandleUploadErknmAttachWebDocumentSaga(action: ActionType<typeof knmHandleUploadErknmAttachWebDocument>) {
  const { approvedValue, document, signs } = action.payload;
  const state: IRootState = yield select();
  const onSaveButtonClick = getKnmOnSaveButtonClick(state);
  const formRef = getKnmFormRef(state);

  if (document && approvedValue) {
    const convertedDocument: string = yield convertFileToBase64(document);
    const signTextArr: string[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const sign of signs) {
      const signText: string = yield convertSignFileToBase64(sign);

      signTextArr.push(signText);
    }

    const files = {
      document: convertedDocument,
      signatures: signTextArr,
    };
    const res: IKnmAttachment = yield fetchKnmDocumentSignaturesUpload(files);
    const attachment: IKnmAttachment = yield fetchKnmDocumentAttachmentUpload({ file: document });

    if (res && approvedValue && attachment) {
      if (onSaveButtonClick) {
        formRef?.current.change('approved', approvedValue);
        onSaveButtonClick();
      }
      yield put(knmAttachWebDocument({ ...attachment }));
      if (res.signed) {
        yield put(
          addNoticeAction({
            renderContent: PASS_SIGNATURE_VALIDATION,
            noticeType: NoticeTypesEnum.SUCCESS,
            messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
          }),
        );
      } else {
        yield put(
          addNoticeAction({
            renderContent: '',
            noticeType: NoticeTypesEnum.SUCCESS,
            messageType: MessageTypesEnum.UPLOAD_DOCUMENT,
          }),
        );
      }
      yield put(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_ATTACH_WEB_DOCUMENT_MODAL));
    } else {
      yield put(
        addNoticeAction({
          renderContent: INVALID_DOCUMENT_SIGNATURE,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.VALIDATE_DOCUMENT,
        }),
      );
    }
  }
}

function* knmCreatePlannedKnmSaga(action: ActionType<typeof knmCreatePlannedKnm>) {
  const { knmParams, setKnmParams, setIsAddKnmToPlanVisible } = action.payload;
  const state: IRootState = yield select();
  const knmPlan = getPlanListPlan(state);
  const knmPlanErknmStatus = getPlanListPlanErknmStatus(state);
  const isKnmPlanErknmStatusInProcess = getIsPlanErknmStatusInProcess(knmPlanErknmStatus);
  const isKnmPlanErknmStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanErknmStatus);
  const isKnmPlanErknmStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanErknmStatus);

  put(knmSetIsSaveOnProcess(false));

  if (knmParams && knmPlan) {
    const startPlanYear = parseDate({
      date: knmPlan?.startDate || '',
      parseFormat: SYSTEM_DATE_FORMAT_FNS,
    })?.getFullYear() as number;
    const endPlanYear = parseDate({
      date: knmPlan?.endDate || '',
      parseFormat: SYSTEM_DATE_FORMAT_FNS,
    })?.getFullYear() as number;
    const getNoticeErrContent = (messageText: string) => ({
      renderContent: messageText,
      noticeType: NoticeTypesEnum.ERROR,
      messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
    });

    if (isKnmPlanErknmStatusInProcess) {
      // TODO: временное снятие блокировки
      // if (checkDeadline(knmPlan.startDate as string)) {
      //   addNotice({
      //     renderContent: KNM_NOT_AVAILABLE_LINK_TO_PLAN_AFTER_OKTOBER,
      //     noticeType: NoticeTypesEnum.ERROR,
      //     messageType: MessageTypesEnum.ADD_KNM_TO_PLAN,
      //   });
      // } else {
      const knmStartYear = parseDate({
        date: knmPlan?.startDate || '',
        parseFormat: SYSTEM_DATE_FORMAT_FNS,
      })?.getFullYear() as number;

      if (knmPlan?.deleted) {
        yield put(addNoticeAction(getNoticeErrContent(KNM_NOT_AVAILABLE_LINK_TO_DELETED_PLAN)));
      } else if (knmStartYear < startPlanYear || knmStartYear > endPlanYear) {
        yield put(
          addNoticeAction(getNoticeErrContent(NOTICE_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS)),
        );
      } else {
        yield put(knmCreate(knmParams));
      }
      // }
      setKnmParams(null);
    } else if (isKnmPlanErknmStatusUnderConsideration) {
      yield put(addNoticeAction(getNoticeErrContent(KNM_NOT_AVAILABLE_LINK_TO_PLAN_UNDER_CONSIDERATION)));
      setKnmParams(null);
    } else if (isKnmPlanErknmStatusConsidered) {
      yield put(addNoticeAction(getNoticeErrContent(KNM_NOT_AVAILABLE_LINK_TO_PLAN_CONSIDERED)));
      setKnmParams(null);
    } else if (startPlanYear >= new Date().getFullYear()) {
      yield put(knmCreate(knmParams));
      setKnmParams(null);
    } else if (startPlanYear === new Date().getFullYear()) {
      setIsAddKnmToPlanVisible(true);
    } else {
      yield put(addNoticeAction(getNoticeErrContent(KNM_NOT_AVAILABLE_LINK_TO_PLAN)));
    }
  }
}

function* onSuccessCallback(data: IKnm) {
  const hasInProcess = data.signatureInfo?.find(i => i.status === KnmSignatureInfoStatusesEnum.IN_PROCESS);

  if (hasInProcess) {
    yield delay(1000);
    yield put(knmGet(onSuccessCallback));
  }
}

function* knmSignUploadModalApplySaga() {
  yield put(knmRemoveVisibleModal(KnmModalsEnum.SIGN_UPLOAD_MODAL));

  yield put(knmGet(onSuccessCallback));
  yield put(
    addNoticeAction({
      renderContent: 'Подпись успешно загружена',
      noticeType: NoticeTypesEnum.SUCCESS,
      messageType: MessageTypesEnum.UPLOAD_SIGN,
    }),
  );
}

function* knmSignSubmitFailSaga(action: ActionType<typeof knmSignSubmitFail>) {
  const { errors } = action.payload;

  const knmSystemType: KnmSystemTypesEnum | undefined = yield select(getKnmSystemType);
  const errorsKeys = errors ? Object.keys(errors) : undefined;
  const isRejectReasonsError = errorsKeys?.some(errorKey => errorKey === 'rejectReasons');

  if (isRejectReasonsError) {
    yield put(
      addNoticeAction({
        renderContent: KNM_SAVE_REJECT_REASONS_ERROR_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.REJECT_KNM_REASONS,
      }),
    );
  }
  const isBasicErrors = errorsKeys?.filter(errorKey => errorKey !== 'rejectReasons').length;

  if (isBasicErrors) {
    yield put(
      addNoticeAction({
        renderContent: knmSystemType === KnmSystemTypesEnum.PM ? PM_SAVE_ERROR_WARNING : KNM_SAVE_ERROR_WARNING,
        noticeType: NoticeTypesEnum.WARNING,
        messageType: MessageTypesEnum.SAVE_KNM,
      }),
    );
  }
}

/**
 * Запрос данных с сервера по датам, недоступным для выбора
 * в полях "Дата начала КНМ" и "Дата окончания КНМ"
 * @param action
 */
function* knmGetExcludeDatesSaga(action: ActionType<typeof knmInfoGetExcludeDates>) {
  function* funcTry() {
    const { data } = action.payload;

    const excludeDates: IErknmInfoExcludeDatesResponse = yield fetchExcludeDates(data);

    yield put(knmInfoSetExcludeDates(excludeDates));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_FETCH_EXCLUDED_DATES,
        }),
      );
    },
  });
}

function* knmGetWorkDaysCountBeforeStart(action: ActionType<typeof knmInfoGetWorkDaysCountBeforeStart>) {
  function* funcTry() {
    const { startDate } = action.payload;

    const currentDate = formatDate({ date: new Date(), dateFormat: SYSTEM_DATE_FORMAT_FNS });

    const workingDaysCountResponse: { count: number } = yield fetchWorkingDaysCount(currentDate, startDate);
    const workingDaysCount = (workingDaysCountResponse?.count as number) ?? 0;

    yield put(knmInfoSetWorkDaysCountBeforeStart(workingDaysCount));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_FETCH_WORKING_DAYS_COUNT,
        }),
      );
    },
  });
}

function* knmGetPlannedActivitiesSaga(action: ActionType<typeof knmInfoGetPlannedActivities>) {
  function* funcTry() {
    const { knoId, supervisionId } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmId(state);

    const result: IKnmInfoPlannedActivities = yield fetchPlannedActivities(
      knoId,
      supervisionId,
      id !== undefined ? id : null,
    );

    yield put(knmInfoSetPlannedActivities(result.plannedActivities));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_FETCH_PLANNED_ACTIVITIES,
        }),
      );
    },
  });
}

function* knmInfoGetNKOForeignAgentDataByOgrnSaga(action: ActionType<typeof knmInfoGetNKOForeignAgentDataByOgrn>) {
  function* funcTry() {
    const { ogrn } = action.payload;
    const result: INKOForeignAgent = yield fetchNKOForeignAgentByOgrn(ogrn);

    yield put(knmInfoSetNKOForeignAgentDataByOgrn(result));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: INTERNAL_SERVER_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_FETCH_NK_FOREIGN_AGENT_BY_OGRN,
        }),
      );
    },
  });
}

function* knmInfoGetIsOrgInSonkoRegistrySaga(action: ActionType<typeof knmInfoGetIsOrgInSonkoRegistry>) {
  function* funcTry() {
    const { ogrn, inn } = action.payload;
    const isPresent: boolean = yield fetchIsOrgInSonkoRegistry(ogrn, inn);

    yield put(knmInfoSetIsOrgInSonkoRegistry(ogrn, inn, isPresent));
  }

  function* funcCatch() {
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.KNM_FETCH_IS_ORG_IN_SONKO_REGISTRY,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* knmUpdateAccessPrivilegeSaga(action: ActionType<typeof knmUpdateAccessPrivilege>) {
  function* funcTry() {
    const { data } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmId(state);

    if (typeof id === 'number') {
      const result: IKnm = yield fetchUpdateAccessPrivilege(id, data);

      yield put(knmSetRawData(result));
      yield put(knmGetHistoryChanges());
      yield put(knmRemoveVisibleModal(KnmModalsEnum.ACCESS_SETTINGS));
    }
  }

  function* funcCatch() {
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.SAVE_KNM,
      }),
    );
  }

  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}

function* knmInfoGetEpguNotificationListSaga(action: ActionType<typeof knmInfoGetEpguNotificationList>) {
  function* funcTry() {
    const pageSize = 6;
    const { organizationGuid, type, shouldIgnoreTotalCount } = action.payload;
    const state: IRootState = yield select();
    const knmId = getKnmId(state);
    const decisionId = getDecisionDataId(state);
    const epguNotificationList = getEpguNotificationList(state);
    const epguNotificationTotalCount = getEpguNotificationTotalCount(state);
    const currentCount: number = epguNotificationList?.[organizationGuid]?.length || 0;
    const totalCount: number | undefined = epguNotificationTotalCount?.[organizationGuid];

    if (
      (knmId !== undefined || decisionId !== undefined) &&
      (totalCount === undefined || currentCount < totalCount || shouldIgnoreTotalCount)
    ) {
      const page = totalCount === undefined ? 0 : Math.floor(currentCount / pageSize);
      const epguNotificationPage: IEpguNotificationPage = yield fetchEpguNotification(
        knmId,
        decisionId?.toString(),
        organizationGuid,
        page,
        pageSize,
        type,
      );

      yield put(knmInfoAddEpguNotificationList(organizationGuid, epguNotificationPage.list));
      yield put(knmInfoSetEpguNotificationTotalCount(organizationGuid, epguNotificationPage.totalCount));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmInfoGetErvkObjectsOfControlSaga(action: ActionType<typeof knmInfoGetErvkObjectsOfControl>) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { cosIdFilter, textFilter, riskCategoryIds, dangerClassIds, isDecision } = action.payload;
    const knoOrganizationDictVersionId = isDecision
      ? getDecisionKnoOrganizationFormValue(state)?.value.dictVersionId
      : getKnmErknmKnoOrganizationDictVersionIdFormValue(state);
    const supervisionTypeDictVersionId = isDecision
      ? getDecisionSupervisionTypeFormValue(state)?.value.dictVersionId
      : getKnmErknmSupervisionTypeDictVersionIdFormValue(state);
    const reasonUuid = !isDecision
      ? getKnmErknmReasonsFormValue(state)
          ?.map(x => x?.ervkUuid)
          ?.find(uuid => uuid)
      : undefined;
    const isPlanned = !isDecision ? getKnmTypeFormValue(state)?.value === KnmTypeCodesEnum.PP : undefined;

    if (knoOrganizationDictVersionId !== undefined && supervisionTypeDictVersionId !== undefined) {
      const ervkObjectsOfControl: IErvkObjectsOfControl[] = yield fetchErvkObjectsOfControl(
        knoOrganizationDictVersionId,
        supervisionTypeDictVersionId,
        cosIdFilter,
        textFilter,
        riskCategoryIds,
        dangerClassIds,
        reasonUuid,
        isPlanned,
      );

      yield put(knmInfoAddErvkObjectOfControl(ervkObjectsOfControl));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function isValidPlannedKnm(knmParams: IKnmParams | null, knmPlan: IKnmPlan | null): string | undefined {
  const knmPlanErknmStatus = getPlanErknmStatusByStatusTitle(knmPlan?.erknmStatus);
  const isKnmPlanErknmStatusInProcess = getIsPlanErknmStatusInProcess(knmPlanErknmStatus);
  const isKnmPlanErknmStatusUnderConsideration: boolean = getIsPlanErknmStatusUnderConsideration(knmPlanErknmStatus);
  const isKnmPlanErknmStatusConsidered: boolean = getIsPlanErknmStatusConsidered(knmPlanErknmStatus);

  if (knmParams && knmPlan) {
    const startPlanYear = parseDate({
      date: knmPlan?.startDate || '',
      parseFormat: SYSTEM_DATE_FORMAT_FNS,
    })?.getFullYear() as number;
    const endPlanYear = parseDate({
      date: knmPlan?.endDate || '',
      parseFormat: SYSTEM_DATE_FORMAT_FNS,
    })?.getFullYear() as number;

    if (isKnmPlanErknmStatusInProcess) {
      const knmStartYear = parseDate({
        date: knmPlan?.startDate || '',
        parseFormat: SYSTEM_DATE_FORMAT_FNS,
      })?.getFullYear() as number;

      if (knmPlan?.deleted) {
        return KNM_NOT_AVAILABLE_LINK_TO_DELETED_PLAN;
      }

      if (knmStartYear < startPlanYear || knmStartYear > endPlanYear) {
        return NOTICE_KNM_NOT_AVAILABLE_LINK_TO_PLAN_YEAR_OF_PLAN_AND_KNM_NOT_EQUALS;
      }

      return undefined;
    }

    if (isKnmPlanErknmStatusUnderConsideration) {
      return KNM_NOT_AVAILABLE_LINK_TO_PLAN_UNDER_CONSIDERATION;
    }

    if (isKnmPlanErknmStatusConsidered) {
      return KNM_NOT_AVAILABLE_LINK_TO_PLAN_CONSIDERED;
    }

    if (startPlanYear >= new Date().getFullYear()) {
      return undefined;
    }

    return KNM_NOT_AVAILABLE_LINK_TO_PLAN;
  }
}

function* knmGetOrdersSaga(action: ActionType<typeof knmGetOrders>) {
  function* funcTry() {
    const { knoId, supervisionId, conditionId, isPm, typeId } = action.payload;
    const currentConditionId: string | undefined = yield select(getKnmOrdersSelectConditionId);
    const currentSelectOptions: IKnmOrderSelectOption[] | undefined = yield select(getKnmOrdersSelectOptions);

    if (currentConditionId !== conditionId || currentSelectOptions?.length === 0) {
      const result: IKnmOrder[] = yield fetchOrders(knoId, supervisionId, conditionId, isPm, typeId);

      const selectOptions: IKnmOrderSelectOption[] = result.map(createKnmOrderSelectOption);

      yield put(knmSetOrders(selectOptions, action.payload.conditionId));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* getIsKnmCanBeSignedSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const knmId = getKnmDataId(state);

    if (isErknm && typeof knmId === 'number') {
      const isCanBeSigned: boolean = yield fetchIsKnmCanBeSigned(knmId);

      yield put(knmSetIsCanBeSigned(isCanBeSigned));
    } else {
      yield put(knmSetIsCanBeSigned(true));
    }
  }

  function* funcCatch() {
    yield put(knmSetIsCanBeSigned(false));
    yield put(
      addNoticeAction({
        renderContent: INTERNAL_SERVER_ERROR,
        noticeType: NoticeTypesEnum.ERROR,
        messageType: MessageTypesEnum.INTERNAL_SERVER,
      }),
    );
  }

  yield sagaWrapper({ funcTry, funcCatch });
}

function* checkKnoSupervisionLinkSaga() {
  function* funcTry() {
    const state: IRootState = yield select();

    const isErknm: boolean = yield getLawInterface(state) === LawInterfacesEnum.FZ_248;
    const knmId = getKnmDataId(state);

    if (isErknm && typeof knmId === 'number') {
      const ervkCorrectionRequiredResponse: IErvkCorrectionRequiredData = yield fetchCheckKnoSupervisionLink(knmId);

      yield put(knmSetCheckKnoSupervisionLink(ervkCorrectionRequiredResponse));
    }
  }

  yield sagaWrapper({ funcTry });
}

function* knmGetInvalidFieldsSaga(action: ActionType<typeof knmGetInvalidFields>) {
  function* funcTry() {
    const fields: string[] = yield fetchInvalidFields(action.payload.id);

    yield put(knmSetInvalidFields(fields));
  }

  function* funcCatch() {
    yield put(knmClearInvalidFields());
  }

  yield sagaWrapper({
    funcTry,
    funcCatch,
  });
}
function* knmCheckChangesSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (typeof knmId === 'number') {
      yield put(knmCheckChangesRequest());

      const isChanges: boolean = yield fetchKnmCheckChanges(knmId);

      yield put(knmCheckChangesSuccess(isChanges));

      yield put(
        addNoticeAction({
          renderContent: isChanges ? 'Сведения обновлены' : 'Все данные актуальны',
          noticeType: NoticeTypesEnum.SUCCESS,
          messageType: MessageTypesEnum.KNM_CHECK_CHANGES,
        }),
      );
      yield put(knmGet());
    }
  }
  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmCheckChangesFailure());
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* knmGetProsecutorOrganisationsSaga(action: ActionType<typeof knmErvkProsecutorGet>) {
  function* funcTry() {
    const { uuid, versionId } = action.payload;

    yield put(knmErvkProsecutorGetRequest());

    const response: IUserOrganisation = yield fetchProsecutorOrganisationsByErvkUuidAndVersion(uuid, versionId);

    yield put(knmErvkProsecutorGetSuccess(response));
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(knmErvkProsecutorGetFailure());
    },
  });
}

function* knmGetKnoCollaboratingOrganisationsSaga(action: ActionType<typeof knmGetKnoCollaboratingOrganisations>) {
  function* funcTry() {
    const { searchQuery, knoLevelCode, organisationsParentId } = action.payload;

    if (searchQuery) {
      const res: IKnoOrganisationsChildrenResponse = yield fetchKnoErvkOrganisationsSearch({
        query: searchQuery,
        knoLevelCode: knoLevelCode === 'ANY' ? null : knoLevelCode || null,
        searchCriteria: Number(searchQuery) ? 'ogrn' : 'name',
        order: 'fullName',
        page: '0',
        size: '15',
      });

      yield put(knmSetKnoCollaboratingOrganisations(res.list, res.page.toString(), res.pageCount, res.totalCount));
    } else {
      if (!knoLevelCode) {
        return;
      }

      const res: IKnoOrganisationsChildrenResponse = yield fetchKnoErvkOrganisationsChildrenWithKnoLevelCode({
        knoLevelCode: knoLevelCode === 'ANY' ? null : knoLevelCode,
        id: organisationsParentId,
        order: 'fullName',
        size: '2000',
      });

      yield put(knmSetKnoCollaboratingOrganisations(res.list, res.page.toString(), res.pageCount, res.totalCount));
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmGetKnoCollaboratingOrganisationsNextPageSaga(
  action: ActionType<typeof knmGetKnoCollaboratingOrganisationsNextPage>,
) {
  function* funcTry() {
    const state: IRootState = yield select();
    const { searchQuery, knoLevelCode } = action.payload;
    const page = getKnmCollaboratingOrganisationsPage(state);
    const currentList = getKnmCollaboratingKnoOrganisations(state);
    const nextPage = (+page + 1).toString();

    if (searchQuery) {
      const res: IKnoOrganisationsChildrenResponse = yield fetchKnoErvkOrganisationsSearch({
        query: searchQuery,
        knoLevelCode: knoLevelCode === 'ANY' ? null : knoLevelCode || null,
        searchCriteria: Number(searchQuery) ? 'ogrn' : 'name',
        order: 'fullName',
        page: nextPage,
        size: '15',
      });

      yield put(
        knmSetKnoCollaboratingOrganisations(
          [...currentList, ...res.list],
          res.page.toString(),
          res.pageCount,
          res.totalCount,
        ),
      );
    } else if (knoLevelCode) {
      const res: IKnoOrganisationsChildrenResponse = yield fetchKnoErvkOrganisationsChildrenWithKnoLevelCode({
        knoLevelCode: knoLevelCode === 'ANY' ? null : knoLevelCode,
        order: 'fullName',
        page: nextPage,
        size: '2000',
      });

      yield put(
        knmSetKnoCollaboratingOrganisations(
          [...currentList, ...res.list],
          res.page.toString(),
          res.pageCount,
          res.totalCount,
        ),
      );
    }
  }

  yield sagaWrapper({
    funcTry,
  });
}

function* knmRestoreLatestSignedVersion() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (knmId) {
      yield fetchKnmRestoreLatestSignedVersion(knmId);
      yield knmReloadSaga();
      yield put(knmGetHistoryChanges());
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* knmSendAgreementSaga() {
  function* funcTry() {
    const state: IRootState = yield select();
    const knmId = getKnmDataId(state);

    if (knmId) {
      const formRef = getKnmFormRef(state);

      if (formRef?.current) {
        formRef.current.submit();

        yield put(knmReload());
        yield put(knmHistoryReload());
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: 'Ошибка обновления данных',
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.KNM_ATTACH_WEB_DOCUMENT,
        }),
      );
    },
  });
}

function* knmDownloadAgreementTemplate(action: ActionType<typeof knmAgreementTemplateDownload>) {
  function* funcTry() {
    const { agreement } = action.payload;
    const state: IRootState = yield select();
    const id = getKnmDataId(state);

    if (id) {
      const agreementData = convertAgreementsFormValuesToData([agreement]);

      if (agreementData) {
        const response: Blob = yield fetchKnmAgreementTemplateDownload(id, agreementData[0], false);
        const file = new Blob([response]);

        saveFile(file, `${id}.docx`);
      }
    }
  }

  yield sagaWrapper({
    funcTry,
    *funcCatch(currentError: IResponseError) {
      yield put(
        addNoticeAction({
          renderContent: currentError.message || currentError.text,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.DOWNLOAD_XML,
        }),
      );
    },
  });
}

function* KnmErknmCancelPrescribeSaga(action: ActionType<typeof knmErknmCancelPrescribe>) {
  function* funcTry() {
    const { data } = action.payload;

    yield fetchErknmPrescribeCancel(data);
    yield put(knmReload());
    yield put(knmRemoveVisibleModal(KnmModalsEnum.ERKNM_PRESCRIBE_CANCEL));
    yield put(knmGetHistoryChanges());
    yield put(
      addNoticeAction({
        renderContent: PRESCRIBE_CANCEL_SUCCESS,
        noticeType: NoticeTypesEnum.SUCCESS,
        messageType: MessageTypesEnum.PASSPORT_SIGN,
      }),
    );
  }
  yield sagaWrapper({
    funcTry,
    *funcCatch() {
      yield put(
        addNoticeAction({
          renderContent: PRESCRIBE_CANCEL_ERROR,
          noticeType: NoticeTypesEnum.ERROR,
          messageType: MessageTypesEnum.PRESCRIBE_CANCEL,
        }),
      );
    },
  });
}
